import type { LogRow as LogRowType } from "../../api/types";
import { formatTime } from "../../utils/format";

const LEVEL_LABEL: Record<LogRowType["level"], string> = {
  ERROR: "ERR",
  WARNING: "WARN",
  INFO: "INFO",
  DEBUG: "DBG",
};

const LEVEL_COLOR: Record<LogRowType["level"], string> = {
  ERROR: "var(--short)",
  WARNING: "var(--warn)",
  INFO: "var(--fg3)",
  DEBUG: "var(--fg3)",
};

const LEVEL_BG: Record<LogRowType["level"], string> = {
  ERROR: "var(--errbg)",
  WARNING: "transparent",
  INFO: "transparent",
  DEBUG: "transparent",
};

// One row in the Logs table — time / level / message / repeat-count badge,
// matching the prototype's monospace log-line layout exactly.
export function LogRow({ log }: { log: LogRowType }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: 10,
        padding: "5px 14px",
        borderBottom: "1px solid var(--line)",
        background: LEVEL_BG[log.level],
      }}
    >
      <span style={{ font: "400 11px 'JetBrains Mono',monospace", color: "var(--fg3)", flex: "none", width: 62 }}>
        {formatTime(log.ts)}
      </span>
      <span
        style={{
          font: "700 9.5px 'Golos Text',sans-serif",
          letterSpacing: ".06em",
          flex: "none",
          width: 42,
          paddingTop: 1,
          color: LEVEL_COLOR[log.level],
        }}
      >
        {LEVEL_LABEL[log.level]}
      </span>
      {log.bot_label && (
        <span
          title={log.bot_instance_id ?? undefined}
          style={{
            font: "700 9.5px 'JetBrains Mono',monospace",
            flex: "none",
            padding: "1px 5px",
            borderRadius: 5,
            background: "var(--surface3)",
            color: "var(--fg3)",
          }}
        >
          {log.bot_label}
        </span>
      )}
      <span style={{ font: "400 11.5px/1.5 'JetBrains Mono',monospace", color: "var(--fg2)", flex: 1, minWidth: 0, wordBreak: "break-word" }}>
        {log.message}
      </span>
      <span
        style={{
          font: "600 10.5px 'JetBrains Mono',monospace",
          flex: "none",
          padding: "1px 6px",
          borderRadius: 5,
          background: "var(--surface3)",
          color: "var(--fg2)",
          visibility: log.repeat_count > 1 ? "visible" : "hidden",
        }}
      >
        ×{log.repeat_count}
      </span>
    </div>
  );
}
